// Mirrors backend DTOs exactly — field-name capitalisation matches ABP's default camelCase JSON.
export enum LicenseStatus { Active = 1, Expired = 2, Revoked = 3, Suspended = 4 }
export enum LicenseType { Purchase = 1, Rental = 2, Subscription = 3 }
export enum LicenseHistoryAction {
  Revoke = 1, Suspend = 2, Reactivate = 3, Extend = 4, BulkAssign = 5, TerminateSessions = 6,
}

export interface ManageLicensesListInput {
  filterText?: string;
  status?: LicenseStatus;
  licenseType?: LicenseType;
  publisherId?: string;
  bookId?: string;
  booksellerId?: string;
  studentId?: string;
  activatedAfter?: string;
  activatedBefore?: string;
  expiresAfter?: string;
  expiresBefore?: string;
  expiringWithinDays?: number;
  isPerpetual?: boolean;
  hasExternalLicenseId?: boolean;
  hasActiveSessions?: boolean;
  atOrOverDeviceLimit?: boolean;
  includeDeleted?: boolean;
  sorting?: string;
  skipCount: number;
  maxResultCount: number;
}

export interface ManageLicenseListDto {
  id: string;
  studentId: string;
  studentEmail: string;
  studentName: string;
  bookId: string;
  bookTitle: string;
  publisherId?: string;
  publisherName?: string;
  booksellerId?: string;
  booksellerName?: string;
  licenseType: LicenseType;
  status: LicenseStatus;
  activationDate?: string;
  expirationDate?: string;
  maxConcurrentDevices: number;
  activeDeviceCount: number;
  externalLicenseId?: string;
  creationTime: string;
  lastModificationTime?: string;
  isDeleted: boolean;
  revokedReason?: string;
  revokedDate?: string;
  lastSyncedAt?: string;
}

export interface BulkLicenseActionResultDto {
  succeeded: string[];
  skipped: { id: string; reason: string }[];
  failed: { id: string; error: string }[];
}

export interface ExtendLicenseDto {
  newExpirationDate?: string | null; // null → perpetual
  reason?: string;
}

export interface BulkLicenseIdsWithReasonDto {
  licenseIds: string[];
  reason: string;
}

export interface BulkExtendLicensesDto {
  licenseIds: string[];
  offsetDays?: number;
  newExpirationDate?: string;
  reason?: string;
}

export interface LicenseActionHistoryDto {
  id: string;
  licenseId: string;
  action: LicenseHistoryAction;
  actorUserId: string;
  actorUserName: string;
  reason?: string;
  previousStatus?: LicenseStatus;
  newStatus?: LicenseStatus;
  previousExpirationDate?: string;
  newExpirationDate?: string;
  metadata?: string;
  timestamp: string;
}

export interface LicenseActiveSessionDto {
  id: string;
  deviceFingerprintHash: string;
  ipAddress?: string;
  startedAt: string;
  lastValidatedAt?: string;
  expiresAt: string;
  chunksAccessed: number;
}

export interface LicenseFilterState {
  q: string;
  status: LicenseStatus | 'all';
  type: LicenseType | 'all';
  expiringWithinDays?: 7 | 30 | 90;
  publisherIds: string[];
  bookIds: string[];
  booksellerIds: string[];
  activatedAfter?: string;
  activatedBefore?: string;
  expiresAfter?: string;
  expiresBefore?: string;
  isPerpetual?: boolean;
  hasExternalLicenseId?: boolean;
  hasActiveSessions?: boolean;
  atOrOverDeviceLimit: boolean;
  includeDeleted: boolean;
  sort: 'expirationDate' | 'activationDate' | 'status' | 'lastModificationTime' | 'activeDeviceCount' | 'studentEmail' | 'bookTitle';
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_LICENSE_FILTERS: LicenseFilterState = {
  q: '',
  status: 'all',
  type: 'all',
  publisherIds: [],
  bookIds: [],
  booksellerIds: [],
  atOrOverDeviceLimit: false,
  includeDeleted: false,
  sort: 'expirationDate',
  dir: 'asc',
  page: 1,
  pageSize: 25,
};
